/**
 * AuthContext.tsx — login, session, identity, and permission state.
 *
 * Owns: current `AuthUser`, role + permission lookup, identity mappings
 *       (Clockify / Aptly user IDs), session refresh, login/logout flows
 *       via Google OAuth + dev-bypass.
 * Used by: every protected route, every permission-guarded action,
 *       App.tsx for the root auth state, integrations that need the
 *       current user's identity in third-party systems.
 * Depends on: backend `/api/auth/*` endpoints, axios with credentials.
 * Notes: Frozen file (operating-rules.md §4). Regressions can lock users
 *       out or expose unauthorized surfaces. Edit only via frozen-file
 *       unlock. Reads `process.env.REACT_APP_API_URL` directly because the
 *       0b-01 central-config migration intentionally left frozen files
 *       untouched.
 */
import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

export interface AuthUser {
  id: number;
  email: string;
  displayName: string;
  avatarUrl: string;
  roleId: number | null;
  roleName: string | null;
  isActive: boolean;
}

export interface Permission {
  type: string; // 'tab' | 'action' | 'metric'
  key: string;
}

export interface IdentityMapping {
  name: string | null;
  id: string | null;
}

export interface Identities {
  clockify?: IdentityMapping;
  aptly?: IdentityMapping;
  [source: string]: IdentityMapping | undefined;
}

interface AuthState {
  loading: boolean;
  authenticated: boolean;
  user: AuthUser | null;
  permissions: Permission[];
  identities: Identities;
}

interface ImpersonationState {
  active: boolean;
  roleId: number | null;
  roleName: string | null;
  permissions: Permission[];
  department: string | null;
}

interface AuthContextType extends AuthState {
  hasPermission: (type: string, key: string) => boolean;
  hasTab: (tab: string) => boolean;
  hasAction: (action: string) => boolean;
  isAdmin: () => boolean;
  isTeamMember: () => boolean;
  isPending: () => boolean;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  impersonation: ImpersonationState;
  impersonateRole: (roleId: number) => Promise<void>;
  setImpersonationDepartment: (dept: string | null) => void;
  stopImpersonating: () => void;
  realUser: AuthUser | null;
}

const AuthContext = createContext<AuthContextType>({
  loading: true,
  authenticated: false,
  user: null,
  permissions: [],
  identities: {},
  hasPermission: () => false,
  hasTab: () => false,
  hasAction: () => false,
  isAdmin: () => false,
  isTeamMember: () => false,
  isPending: () => false,
  logout: async () => {},
  refresh: async () => {},
  impersonation: { active: false, roleId: null, roleName: null, permissions: [], department: null },
  impersonateRole: async () => {},
  setImpersonationDepartment: () => {},
  stopImpersonating: () => {},
  realUser: null,
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    loading: true,
    authenticated: false,
    user: null,
    permissions: [],
    identities: {},
  });

  const [impersonation, setImpersonation] = useState<ImpersonationState>({
    active: false,
    roleId: null,
    roleName: null,
    permissions: [],
    department: null,
  });

  const fetchAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/auth/me`, { withCredentials: true });
      // 0b-02c: backend now wraps in canonical { success: true, data: { authenticated, user, permissions, identities } }
      const payload = res.data.data;
      if (payload?.authenticated) {
        setState({
          loading: false,
          authenticated: true,
          user: payload.user,
          permissions: payload.permissions || [],
          identities: payload.identities || {},
        });
      } else {
        setState({ loading: false, authenticated: false, user: null, permissions: [], identities: {} });
      }
    } catch {
      setState({ loading: false, authenticated: false, user: null, permissions: [], identities: {} });
    }
  }, []);

  useEffect(() => {
    fetchAuth();
  }, [fetchAuth]);

  // The effective user/permissions — applies impersonation overlay when active
  const effectiveRoleName = impersonation.active ? impersonation.roleName : state.user?.roleName;
  const effectivePermissions = impersonation.active ? impersonation.permissions : state.permissions;
  const effectiveUser: AuthUser | null = useMemo(() => impersonation.active && state.user
    ? { ...state.user, roleName: impersonation.roleName, roleId: impersonation.roleId }
    : state.user, [impersonation.active, impersonation.roleName, impersonation.roleId, state.user]);

  const hasPermission = useCallback((type: string, key: string) => {
    return effectivePermissions.some(p => p.type === type && p.key === key);
  }, [effectivePermissions]);

  const hasTab = useCallback((tab: string) => {
    return hasPermission('tab', `tab:${tab.toLowerCase()}`);
  }, [hasPermission]);

  const hasAction = useCallback((action: string) => {
    return hasPermission('action', `action:${action}`);
  }, [hasPermission]);

  const isAdmin = useCallback(() => {
    const rn = effectiveRoleName?.toLowerCase() || '';
    return rn === 'super admin' || rn === 'admin';
  }, [effectiveRoleName]);

  const isTeamMember = useCallback(() => {
    const rn = effectiveRoleName?.toLowerCase() || '';
    return rn === 'team member' || rn === 'viewer';
  }, [effectiveRoleName]);

  const isPending = useCallback(() => {
    return state.authenticated && effectiveUser?.roleId === null;
  }, [state.authenticated, effectiveUser]);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_BASE_URL}/auth/logout`, {}, { withCredentials: true });
    } catch (err) { console.error('AuthContext: logout request failed', err); }
    setState({ loading: false, authenticated: false, user: null, permissions: [], identities: {} });
    setImpersonation({ active: false, roleId: null, roleName: null, permissions: [], department: null });
  }, []);

  const impersonateRole = useCallback(async (roleId: number) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/users/roles/${roleId}`, { withCredentials: true });
      // 0b-02c: backend now wraps in canonical { success: true, data: { ...role, permissions } }
      const role = res.data.data;
      const perms: Permission[] = (role.permissions || [])
        .filter((p: any) => p.allowed)
        .map((p: any) => ({ type: p.permission_type, key: p.permission_key }));
      setImpersonation(prev => ({
        active: true,
        roleId: role.id,
        roleName: role.name,
        permissions: perms,
        department: prev.department,
      }));
    } catch (err) {
      console.error('[auth] Failed to impersonate role:', err);
    }
  }, []);

  const stopImpersonating = useCallback(() => {
    setImpersonation({ active: false, roleId: null, roleName: null, permissions: [], department: null });
  }, []);

  const setImpersonationDepartment = useCallback((dept: string | null) => {
    setImpersonation(prev => ({ ...prev, department: dept }));
  }, []);

  // Check if the real (non-impersonated) user is an admin
  const realIsAdmin = useMemo(() => {
    const rn = state.user?.roleName?.toLowerCase() || '';
    return rn === 'super admin' || rn === 'admin';
  }, [state.user?.roleName]);

  const value = useMemo(() => ({
    ...state,
    user: effectiveUser,
    permissions: effectivePermissions,
    hasPermission,
    hasTab,
    hasAction,
    isAdmin,
    isTeamMember,
    isPending,
    logout,
    refresh: fetchAuth,
    impersonation,
    impersonateRole: realIsAdmin ? impersonateRole : async () => {},
    setImpersonationDepartment: realIsAdmin ? setImpersonationDepartment : () => {},
    stopImpersonating,
    realUser: state.user,
  }), [
    state,
    effectiveUser,
    effectivePermissions,
    hasPermission,
    hasTab,
    hasAction,
    isAdmin,
    isTeamMember,
    isPending,
    logout,
    fetchAuth,
    impersonation,
    realIsAdmin,
    impersonateRole,
    setImpersonationDepartment,
    stopImpersonating,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

/**
 * Check if the current user has a specific permission (e.g. 'tab:inbox', 'action:create_process').
 * Use for LockedFeature and other permission-gated UI.
 */
export function useHasPermission(permission: string): boolean {
  const { permissions } = useAuth();
  return permissions?.some((p) => p.key === permission) ?? false;
}

export default AuthContext;
